"use client";

import {type DialogProps} from "@radix-ui/react-dialog";
import {Command as CommandPrimitive} from "cmdk";
import {Search} from "lucide-react";

import {Dialog, DialogContent} from "@/components/ui/dialog";
import clsxm from "@/lib/clsxm";
import {type ComponentPropsWithoutRef, type FC, type HTMLAttributes, type RefObject} from "react";

const Command: FC<ComponentPropsWithoutRef<typeof CommandPrimitive> & {ref?: RefObject<HTMLDivElement>}> = ({
  ref,
  className,
  ...props
}) => (
  <CommandPrimitive
    ref={ref}
    className={clsxm(
      "bg-popover text-popover-foreground flex size-full flex-col overflow-hidden rounded-md",
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({children, ...props}: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="**:[[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:size-5 [&_[cmdk-item]_svg]:size-5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput: FC<
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {ref?: RefObject<HTMLInputElement>}
> = ({ref, className, ...props}) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <Search className="mr-2 size-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={clsxm(
        "placeholder:text-muted-foreground flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList: FC<ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {ref?: RefObject<HTMLDivElement>}> = ({
  ref,
  className,
  ...props
}) => (
  <CommandPrimitive.List
    ref={ref}
    className={clsxm("max-h-75 overflow-x-hidden overflow-y-auto", className)}
    {...props}
  />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty: FC<
  ComponentPropsWithoutRef<typeof CommandPrimitive.Empty> & {ref?: RefObject<HTMLDivElement>}
> = props => <CommandPrimitive.Empty ref={props.ref} className="py-6 text-center text-sm" {...props} />;

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup: FC<ComponentPropsWithoutRef<typeof CommandPrimitive.Group> & {ref?: RefObject<HTMLDivElement>}> = ({
  ref,
  className,
  ...props
}) => (
  <CommandPrimitive.Group
    ref={ref}
    className={clsxm(
      "text-foreground **:[[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium",
      className,
    )}
    {...props}
  />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator: FC<
  ComponentPropsWithoutRef<typeof CommandPrimitive.Separator> & {ref?: RefObject<HTMLDivElement>}
> = ({ref, className, ...props}) => (
  <CommandPrimitive.Separator ref={ref} className={clsxm("bg-border -mx-1 h-px", className)} {...props} />
);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem: FC<ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {ref?: RefObject<HTMLDivElement>}> = ({
  ref,
  className,
  ...props
}) => (
  <CommandPrimitive.Item
    ref={ref}
    className={clsxm(
      "data-[selected='true']:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className,
    )}
    {...props}
  />
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({className, ...props}: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={clsxm("text-muted-foreground ml-auto text-xs tracking-widest", className)} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
